import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Building2, TrendingUp, AlertTriangle, Users, Briefcase, Snowflake, Activity,
  ChevronRight, ExternalLink, HelpCircle, HelpingHand, Layers, Target,
  BarChart3, Filter, Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, Legend,
} from 'recharts';
import MetricInfo, { METRIC_INFO_TEXTS } from './MetricInfo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Palettes ────────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  ok:       { dot: 'bg-emerald-500', ring: 'hover:ring-emerald-300', text: 'text-emerald-700', label: 'OK' },
  warning:  { dot: 'bg-amber-500',   ring: 'hover:ring-amber-300',   text: 'text-amber-700',   label: 'Atención' },
  critical: { dot: 'bg-red-500',     ring: 'hover:ring-red-300',     text: 'text-red-700',     label: 'Crítico' },
};

const HEALTH_ICONS = {
  pipeline_open: Briefcase,
  pipeline_coverage: Target,
  no_activity: Activity,
  prospects_unworked: HelpingHand,
  cooling: Snowflake,
  no_contacts: Users,
};

const eur = (n) => {
  if (!n) return '0 €';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${Math.round(n / 1_000)} k€`;
  return `${Math.round(n)} €`;
};

const TooltipBadge = ({ label, tooltipText }) => (
  <div className="inline-flex items-center gap-0.5">
    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">{label}</Badge>
    {tooltipText && <MetricInfo text={tooltipText} testId={`info-badge-${label.toLowerCase()}`} ariaLabel={label} />}
  </div>
);

// ─── Health card ─────────────────────────────────────────────────────────────
const HealthCard = ({ item, onOpen }) => {
  const style = item.status ? (STATUS_STYLES[item.status] || null) : null;
  const Icon = HEALTH_ICONS[item.key] || Activity;
  const infoText = METRIC_INFO_TEXTS[item.key];
  const showPct = item.pct !== null && item.pct !== undefined;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`text-left w-full bg-card border rounded-lg p-4 transition-all hover:shadow-md ring-1 ring-transparent ${style?.ring || 'hover:ring-slate-300'} group`}
      data-testid={`health-card-${item.key}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5">
          <Icon className={`w-4 h-4 ${style?.text || 'text-slate-600'}`} />
          <span className="text-xs font-medium text-muted-foreground">{item.label}</span>
          {infoText && (
            <MetricInfo text={infoText} testId={`info-${item.key}`} ariaLabel={`Información sobre ${item.label}`} />
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <TooltipBadge label="Stock" tooltipText={METRIC_INFO_TEXTS.stock} />
          {style && <span className={`w-2 h-2 rounded-full ${style.dot}`} title={style.label} />}
        </div>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold">{item.value}</span>
        {item.total !== undefined && <span className="text-xs text-muted-foreground">de {item.total}</span>}
        {showPct && <>
          <span className="text-xs text-muted-foreground">·</span>
          <span className={`text-sm font-medium ${style?.text || 'text-slate-700'}`}>{item.pct}%</span>
        </>}
      </div>
      {item.key === 'pipeline_open' && (
        <div className="mt-1 text-[11px] text-slate-600">
          Importe: <strong>{eur(item.amount_eur)}</strong>
          {item.no_amount_count > 0 && <span className="text-amber-700 ml-1">· {item.no_amount_count} sin importe</span>}
        </div>
      )}
      {showPct && (
        <div className="mt-2 h-1.5 w-full bg-muted rounded overflow-hidden">
          <div className={`h-full ${style?.dot || 'bg-slate-400'}`} style={{ width: `${Math.min(item.pct, 100)}%` }} />
        </div>
      )}
      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
        <span className={style?.text || ''}>{style?.label || '\u00A0'}</span>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Ver lista <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </button>
  );
};

// ─── Age buckets chart ──────────────────────────────────────────────────────
const AgeBucketsChart = ({ items, onClick }) => (
  <div className="h-[220px]">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={items} onClick={(e) => e?.activePayload?.[0]?.payload?.key && onClick(e.activePayload[0].payload.key)}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer">
          {items.map(r => <Cell key={r.key} fill={r.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);



// ─── Matrix Heatmap ──────────────────────────────────────────────────────────
const MatrixHeatmap = ({ matrix, onClick }) => {
  const { rows, cols, col_labels, grid, row_colors } = matrix;
  const maxVal = Math.max(1, ...rows.flatMap(rk => cols.map(ck => grid[rk]?.[ck] || 0)));
  const cellColor = (v) => {
    if (v === 0) return 'bg-muted/20 text-muted-foreground';
    const intensity = v / maxVal;
    if (intensity > 0.66) return 'bg-slate-800 text-white';
    if (intensity > 0.33) return 'bg-slate-500 text-white';
    return 'bg-slate-200 text-slate-800';
  };
  return (
    <div className="overflow-x-auto" data-testid="matrix-heatmap">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="text-left py-1 pr-2 font-medium text-muted-foreground">Estado \ Actividad</th>
            {cols.map(ck => (
              <th key={ck} className="px-1 py-1 text-center font-medium text-muted-foreground">{col_labels[ck]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(rk => (
            <tr key={rk}>
              <td className="py-1 pr-2">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: row_colors[rk] }} />
                  {rk}
                </span>
              </td>
              {cols.map(ck => {
                const v = grid[rk]?.[ck] || 0;
                return (
                  <td key={ck} className="px-0.5 py-0.5">
                    <button
                      type="button"
                      onClick={() => v > 0 && onClick(rk, ck)}
                      disabled={v === 0}
                      className={`w-full h-8 rounded flex items-center justify-center font-semibold ${cellColor(v)} ${v > 0 ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                      data-testid={`matrix-cell-${rk}-${ck}`}
                    >
                      {v}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── ABC top list ────────────────────────────────────────────────────────────
const ABCList = ({ abc, onOpen }) => {
  if (!abc.top || abc.top.length === 0) {
    return <div className="text-xs text-muted-foreground p-4 text-center">Ninguna empresa con pipeline abierto</div>;
  }
  const classColors = { A: 'bg-emerald-500', B: 'bg-amber-500', C: 'bg-slate-400' };
  return (
    <div data-testid="abc-list">
      <div className="flex items-center justify-between text-xs mb-3 pb-2 border-b">
        <div className="text-muted-foreground">Total pipeline abierto: <strong className="text-foreground">{eur(abc.total_amount_eur)}</strong></div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"/>A {abc.counts.A}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500"/>B {abc.counts.B}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-400"/>C {abc.counts.C}</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {abc.top.map(row => (
          <button
            key={row.id}
            type="button"
            onClick={() => onOpen(row.id)}
            className="w-full text-left flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/40"
            data-testid={`abc-row-${row.id}`}
          >
            <span className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white ${classColors[row.class] || 'bg-slate-400'}`}>{row.class}</span>
            <span className="flex-1 truncate text-sm font-medium">{row.name}</span>
            <span className="text-xs text-muted-foreground">{row.owner}</span>
            <span className="text-xs text-muted-foreground">{row.opp_count} opps</span>
            <span className="text-sm font-semibold w-20 text-right">{eur(row.amount_eur)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Owner load bars ─────────────────────────────────────────────────────────
const OwnerLoadBars = ({ items, onSegmentClick }) => {
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div className="space-y-2.5" data-testid="owner-load-bars">
      {items.map(o => {
        const na = o.breakdown?.no_activity || 0;
        const cool = o.breakdown?.cooling || 0;
        const total = o.value;
        const ok = Math.max(0, total - na - cool);
        const width = (total / max) * 100;
        const isUnassigned = o.key === '__unassigned__';
        return (
          <div key={o.key} data-testid={`owner-row-${o.key}`}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <button
                onClick={() => onSegmentClick(o.key, null)}
                className={`font-medium truncate max-w-[160px] hover:underline ${isUnassigned ? 'text-red-700' : ''}`}
                data-testid={`owner-bar-${o.key}`}
              >
                {o.label}
              </button>
              <span className="text-muted-foreground">
                {na > 0 && <span className="text-amber-700 mr-2">Sin actividad {na}</span>}
                {cool > 0 && <span className="text-blue-700 mr-2">Frías {cool}</span>}
                <span className="font-medium text-foreground">{total}</span>
              </span>
            </div>
            <div className={`flex h-5 rounded overflow-hidden ${isUnassigned ? 'bg-red-100' : 'bg-muted/40'}`} style={{ width: `${width}%`, minWidth: '18px' }}>
              {isUnassigned ? (
                <button onClick={() => onSegmentClick(o.key, null)} className="h-full w-full bg-red-500" />
              ) : (
                <>
                  {ok > 0 && <button onClick={() => onSegmentClick(o.key, null)} className="h-full bg-emerald-500" style={{ width: `${(ok / total) * 100}%` }} data-testid={`owner-seg-${o.key}-ok`} />}
                  {na > 0 && <button onClick={() => onSegmentClick(o.key, 'no_activity')} className="h-full bg-amber-500" style={{ width: `${(na / total) * 100}%` }} data-testid={`owner-seg-${o.key}-no-activity`} />}
                  {cool > 0 && <button onClick={() => onSegmentClick(o.key, 'cooling')} className="h-full bg-blue-500" style={{ width: `${(cool / total) * 100}%` }} data-testid={`owner-seg-${o.key}-cooling`} />}
                </>
              )}
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] pt-2 border-t">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"/> OK</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500"/> Sin actividad</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500"/> Enfriándose</span>
      </div>
    </div>
  );
};

// ─── Portfolio maturity block ────────────────────────────────────────────────
const PortfolioMaturityBlock = ({ data, total, onClick }) => {
  const { with_open_pipeline: openG, only_closed_history: closedG, no_opps_ever: noneG } = data;
  const groups = [
    { key: 'with_open_pipeline', label: openG.label, value: openG.value, color: openG.color, subtitle: `Importe abierto: ${eur(openG.amount_eur)}`, filter_url: openG.filter_url },
    { key: 'only_closed_history', label: closedG.label, value: closedG.value, color: closedG.color, subtitle: `Ganada alguna vez: ${closedG.breakdown.ever_won.value} · Nunca ganada: ${closedG.breakdown.never_won.value}`, filter_url: closedG.filter_url, breakdown: closedG.breakdown },
    { key: 'no_opps_ever', label: noneG.label, value: noneG.value, color: noneG.color, subtitle: 'Cuentas por abrir', filter_url: noneG.filter_url },
  ];
  const max = Math.max(1, ...groups.map(g => g.value));
  return (
    <div className="space-y-3" data-testid="portfolio-maturity">
      {groups.map(g => (
        <div key={g.key} data-testid={`maturity-${g.key}`}>
          <button
            type="button"
            onClick={() => g.value > 0 && onClick(g.key)}
            disabled={g.value === 0}
            className="w-full text-left group"
          >
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: g.color }} />
                <span className="font-medium group-hover:underline">{g.label}</span>
              </span>
              <span className="text-muted-foreground">
                <strong className="text-foreground">{g.value}</strong> <span className="text-[10px]">de {total}</span>
              </span>
            </div>
            <div className="h-4 bg-muted/40 rounded overflow-hidden">
              <div className="h-full rounded" style={{ width: `${(g.value / max) * 100}%`, backgroundColor: g.color }} />
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{g.subtitle}</div>
          </button>
          {g.breakdown && g.value > 0 && (
            <div className="ml-4 mt-1.5 space-y-1">
              <button
                type="button"
                onClick={() => onClick('only_closed_ever_won')}
                disabled={g.breakdown.ever_won.value === 0}
                className="w-full text-left text-[11px] text-muted-foreground hover:text-foreground flex items-center justify-between"
                data-testid="maturity-only_closed_ever_won"
              >
                <span className="flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-sm bg-emerald-500" /> {g.breakdown.ever_won.label}
                </span>
                <strong>{g.breakdown.ever_won.value}</strong>
              </button>
              <button
                type="button"
                onClick={() => onClick('only_closed_never_won')}
                disabled={g.breakdown.never_won.value === 0}
                className="w-full text-left text-[11px] text-muted-foreground hover:text-foreground flex items-center justify-between"
                data-testid="maturity-only_closed_never_won"
              >
                <span className="flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-sm bg-red-500" /> {g.breakdown.never_won.label}
                </span>
                <strong>{g.breakdown.never_won.value}</strong>
              </button>
            </div>
          )}
        </div>
      ))}
      <div className="text-[10px] text-muted-foreground pt-2 border-t">
        Los 3 grupos son excluyentes y suman el total del ámbito ({groups.reduce((s, g) => s + g.value, 0)} / {total}).
      </div>
    </div>
  );
};

// ─── Data quality bars ────────────────────────────────────────────────────────
const DataQualityBars = ({ dq, onClick }) => {
  const items = Object.values(dq || {});
  return (
    <div className="space-y-2" data-testid="dq-bars">
      {items.map(row => {
        const color = row.pct >= 75 ? '#10b981' : row.pct >= 50 ? '#f59e0b' : '#ef4444';
        return (
          <button
            key={row.key}
            type="button"
            onClick={() => onClick(row.key)}
            className="w-full text-left group"
            data-testid={`dq-${row.key}`}
          >
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="font-medium group-hover:underline">{row.label}</span>
              <span className="text-muted-foreground">
                {row.with_value} / {row.total} · <span style={{ color }} className="font-medium">{row.pct}%</span>
              </span>
            </div>
            <div className="h-3 bg-muted/40 rounded overflow-hidden">
              <div className="h-full transition-opacity group-hover:opacity-80" style={{ width: `${row.pct}%`, backgroundColor: color }} />
            </div>
            {row.without_value > 0 && (
              <div className="text-[10px] text-muted-foreground mt-0.5">Sin: <strong>{row.without_value}</strong></div>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ─── Action Table ────────────────────────────────────────────────────────────
const ActionTable = ({ rows, mode, onOpenCompany, onExport }) => {
  if (!rows || rows.length === 0) {
    const emptyMap = {
      prospects_no_activity: 'No hay prospectos sin actividad 👌',
      cooling: 'Ninguna cuenta enfriándose 🎉',
      open_opps_stale: 'Todas las opps abiertas tienen actividad reciente',
      opps_missing_data: 'Todas las opps abiertas tienen datos completos',
      incomplete_records: 'Todas las fichas están razonablemente completas',
    };
    return <div className="p-6 text-center text-sm text-muted-foreground">{emptyMap[mode] || 'Sin resultados'}</div>;
  }
  return (
    <div data-testid={`action-table-${mode}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="text-xs text-muted-foreground">{rows.length} empresas</div>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onExport(mode, rows)} data-testid={`export-${mode}`}>
          <Download className="w-3.5 h-3.5 mr-1" /> Exportar CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b bg-muted/30">
            <tr>
              <th className="text-left px-3 py-2">Empresa</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">Sector</th>
              <th className="text-left px-3 py-2">Responsable</th>
              <th className="text-right px-3 py-2">Últ. actividad</th>
              <th className="text-right px-3 py-2">Pipeline abierto</th>
              <th className="text-right px-3 py-2 w-16">Acción</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`action-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.status}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.sector}</td>
                <td className={`px-3 py-2 ${r.owner === 'Sin asignar' ? 'text-red-700 font-medium' : 'text-muted-foreground'}`}>{r.owner}</td>
                <td className="px-3 py-2 text-right">
                  {r.days_since_last_activity === null || r.days_since_last_activity === undefined
                    ? <span className="text-muted-foreground italic">nunca</span>
                    : <span className={r.days_since_last_activity > 180 ? 'text-red-700 font-semibold' : ''}>{r.days_since_last_activity}d</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.open_pipeline_count > 0
                    ? <span><strong>{eur(r.open_pipeline_amount)}</strong> <span className="text-muted-foreground text-xs">· {r.open_pipeline_count}</span></span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onOpenCompany(r.id)} data-testid={`open-company-${r.id}`} title="Abrir ficha empresa">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Definitions panel ───────────────────────────────────────────────────────
const DefinitionsPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="link" size="sm" className="text-xs h-7 px-2" data-testid="definitions-link">
        <HelpCircle className="w-3.5 h-3.5 mr-1" />
        ¿Cómo se calculan estas métricas?
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="definitions-panel">
      <SheetHeader>
        <SheetTitle>Definiciones — Analítica de Empresas</SheetTitle>
      </SheetHeader>
      <div className="mt-4 space-y-4 text-sm">
        {[
          ['Pipeline abierto', METRIC_INFO_TEXTS.pipeline_open],
          ['Cobertura de pipeline', METRIC_INFO_TEXTS.pipeline_coverage],
          ['Cuentas sin actividad', METRIC_INFO_TEXTS.no_activity],
          ['Prospectos sin trabajar', METRIC_INFO_TEXTS.prospects_unworked],
          ['Cuentas enfriándose', METRIC_INFO_TEXTS.cooling],
          ['Empresas sin contactos', METRIC_INFO_TEXTS.no_contacts],
          ['Antigüedad última actividad', METRIC_INFO_TEXTS.age_buckets],
          ['Madurez de cartera', METRIC_INFO_TEXTS.portfolio_maturity],
          ['Matriz Estado × Actividad', METRIC_INFO_TEXTS.matrix],
          ['ABC por pipeline', METRIC_INFO_TEXTS.abc],
          ['Carga por responsable', METRIC_INFO_TEXTS.by_owner_load],
          ['Actividad en periodo', METRIC_INFO_TEXTS.company_flow],
          ['Calidad de dato', METRIC_INFO_TEXTS.company_data_quality],
          ['Requieren acción', METRIC_INFO_TEXTS.company_action_list],
          ['Etiqueta Stock', METRIC_INFO_TEXTS.stock],
          ['Etiqueta Flujo', METRIC_INFO_TEXTS.flujo],
        ].map(([title, text]) => (
          <div key={title}>
            <div className="font-semibold text-sm mb-0.5">{title}</div>
            <p className="text-xs text-muted-foreground leading-relaxed">{text}</p>
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);

// ─── CSV Export ──────────────────────────────────────────────────────────────
const rowsToCsv = (rows) => {
  const cols = ['name', 'status', 'sector', 'owner', 'days_since_last_activity', 'open_pipeline_count', 'open_pipeline_amount', 'last_activity_at'];
  const header = ['Empresa', 'Estado', 'Sector', 'Responsable', 'Días desde últ. actividad', 'Nº opps abiertas', 'Importe pipeline abierto (€)', 'Última actividad'];
  const csv = [header.join(';')];
  rows.forEach(r => {
    csv.push(cols.map(c => {
      const v = r[c];
      if (v === null || v === undefined) return '';
      return String(v).replaceAll(';', ',').replaceAll('\n', ' ');
    }).join(';'));
  });
  return csv.join('\n');
};
const downloadCsv = (filename, content) => {
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = filename;
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const NewOppsDot = (p) => {
  const { cx, cy, payload } = p;
  return <circle cx={cx} cy={cy} r={payload.partial ? 5 : 3} fill={payload.partial ? '#fff' : '#3b82f6'} stroke="#3b82f6" strokeWidth={payload.partial ? 2 : 1} />;
};

// ─── Main ────────────────────────────────────────────────────────────────────
const CompaniesAnalytics = ({ clientTypeFilter }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('external');
  const [period, setPeriod] = useState('year');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [actionTab, setActionTab] = useState('prospects_no_activity');

  useEffect(() => { fetchAnalytics(); }, [scope, period, ownerFilter]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const params = new URLSearchParams();
      params.append('scope', scope);
      params.append('period', period);
      if (ownerFilter) params.append('owner', ownerFilter);
      const { data } = await axios.get(`${API}/analytics/companies/v2?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching companies analytics v2:', err);
    } finally {
      setLoading(false);
    }
  };

  const owners = useMemo(() => {
    if (!analytics?.by_owner) return [];
    return analytics.by_owner.filter(o => o.key !== '__unassigned__').map(o => o.key);
  }, [analytics]);

  // Navigate list preserving scope on URL
  const navigateToList = (params) => {
    const q = new URLSearchParams();
    q.set('scope', scope);
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, v);
    });
    // Mark current analytics view so back returns here
    const analyticsUrl = new URLSearchParams(window.location.search);
    analyticsUrl.set('view', 'analytics');
    window.history.replaceState(null, '', `/companies?${analyticsUrl.toString()}`);
    navigate(`/companies?${q.toString()}`);
  };

  const handleOpenHealth = (item) => {
    const map = {
      pipeline_open: { has_open_opp: 'true' },
      pipeline_coverage: { has_open_opp: 'true' },
      no_activity: { no_activity: 'true' },
      prospects_unworked: { status: 'Prospecto', no_activity: 'true' },
      cooling: { cooling: 'true' },
      no_contacts: { no_contacts: 'true' },
    };
    navigateToList(map[item.key] || {});
  };

  const AGE_TO_URL = { '0_30':'0-30','31_90':'31-90','91_180':'91-180','181_365':'181-365','gt_365':'gt-365','nunca':'nunca' };
  const handleAgeClick = (key) => navigateToList({ activity_bucket: AGE_TO_URL[key] });
  const handleMaturityClick = (groupKey) => {
    if (groupKey === 'with_open_pipeline') navigateToList({ has_open_opp: 'true' });
    else navigateToList({ maturity: groupKey });
  };
  const handleMatrixClick = (statusRow, ageCol) => navigateToList({
    status: statusRow === 'Sin asignar' ? '__unassigned__' : statusRow,
    activity_bucket: AGE_TO_URL[ageCol],
  });
  const handleOwnerBarClick = (ownerKey, segKey) => {
    const params = ownerKey === '__unassigned__' ? { owner: '__unassigned__' } : { owner: ownerKey };
    if (segKey === 'no_activity') params.no_activity = 'true';
    if (segKey === 'cooling') params.cooling = 'true';
    navigateToList(params);
  };
  const handleDqClick = (key) => navigateToList({ missing: key });
  const handleOpenCompany = (id) => navigateToList({ companyId: id });

  const handleExportTab = (mode, rows) => {
    downloadCsv(`empresas-${mode}-${new Date().toISOString().slice(0,10)}.csv`, rowsToCsv(rows));
  };

  if (loading && !analytics) return <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">Cargando analíticas…</div>;
  if (!analytics) return <div className="text-center py-12 text-muted-foreground">No hay datos disponibles</div>;

  const { health, summary, data_quality, age_buckets, portfolio_maturity, matrix, abc, by_owner, flow, requieren_accion } = analytics;
  const dqTop = data_quality?.sector?.pct ?? 0;

  return (
    <div className="space-y-6" data-testid="companies-analytics">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Analítica de Empresas
          </h2>
          <p className="text-xs text-muted-foreground">
            <strong>{summary.total_entities}</strong> {summary.entity_kind === 'company' ? 'empresas' : 'unidades TYPSA'} en el ámbito
            {data_quality && (
              <> · Calidad de dato media:{' '}
                <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {dqTop}%</span>
                <MetricInfo text={METRIC_INFO_TEXTS.company_data_quality} testId="info-dq-header" ariaLabel="Calidad de dato" />
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border p-1 bg-muted/30" data-testid="scope-toggle">
            <Button
              variant={scope === 'external' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setScope('external')}
              className="h-7 px-3 text-xs"
              data-testid="scope-external-btn"
            >
              Cartera externa
            </Button>
            <Button
              variant={scope === 'typsa' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setScope('typsa')}
              className="h-7 px-3 text-xs"
              data-testid="scope-typsa-btn"
            >
              Cobertura TYPSA
            </Button>
          </div>
          <DefinitionsPanel />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="period-select">
              <SelectValue placeholder="Periodo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el histórico</SelectItem>
              <SelectItem value="year">Últ. 12 meses</SelectItem>
              <SelectItem value="quarter">Últ. 3 meses</SelectItem>
              <SelectItem value="month">Últ. mes</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-[10px]" data-testid="cartera-completa-tag">Cartera completa · el periodo solo aplica a &laquo;Actividad en periodo&raquo;</Badge>
          <Select value={ownerFilter || 'all'} onValueChange={(v) => setOwnerFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[190px] h-8 text-xs" data-testid="owner-select">
              <SelectValue placeholder="Responsable" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los responsables</SelectItem>
              {owners.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* BLOCK 1: 6 health cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="block-health">
        {Object.values(health).map(h => (
          <HealthCard key={h.key} item={h} onOpen={handleOpenHealth} />
        ))}
      </div>

      {/* BLOCK 2: Charts */}
      <div className="grid md:grid-cols-2 gap-5" data-testid="block-charts">
        <Card data-testid="chart-age">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Antigüedad de la última actividad
              <MetricInfo text={METRIC_INFO_TEXTS.age_buckets} testId="info-age-buckets" ariaLabel="Antigüedad última actividad" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AgeBucketsChart items={age_buckets} onClick={handleAgeClick} />
          </CardContent>
        </Card>

        <Card data-testid="chart-portfolio-maturity">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Madurez de cartera
              <MetricInfo text={METRIC_INFO_TEXTS.portfolio_maturity} testId="info-portfolio-maturity" ariaLabel="Madurez de cartera" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PortfolioMaturityBlock data={portfolio_maturity} total={summary.total_entities} onClick={handleMaturityClick} />
          </CardContent>
        </Card>

        <Card className="md:col-span-2" data-testid="chart-matrix">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Matriz Estado × Actividad
              <MetricInfo text={METRIC_INFO_TEXTS.matrix} testId="info-matrix" ariaLabel="Matriz estado actividad" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MatrixHeatmap matrix={matrix} onClick={handleMatrixClick} />
          </CardContent>
        </Card>

        <Card data-testid="chart-abc">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              ABC por pipeline abierto (top 10)
              <MetricInfo text={METRIC_INFO_TEXTS.abc} testId="info-abc" ariaLabel="ABC" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ABCList abc={abc} onOpen={handleOpenCompany} />
          </CardContent>
        </Card>

        <Card data-testid="chart-owner-load">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Carga por responsable
              <MetricInfo text={METRIC_INFO_TEXTS.by_owner_load} testId="info-owner-load" ariaLabel="Carga por responsable" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[280px] overflow-y-auto pr-1">
              <OwnerLoadBars items={by_owner} onSegmentClick={handleOwnerBarClick} />
            </div>
          </CardContent>
        </Card>

        {data_quality && (
        <Card data-testid="chart-dq">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Calidad de dato
              <MetricInfo text={METRIC_INFO_TEXTS.company_data_quality} testId="info-company-dq" ariaLabel="Calidad de dato empresas" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DataQualityBars dq={data_quality} onClick={handleDqClick} />
          </CardContent>
        </Card>
        )}

        <Card data-testid="chart-flow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4" /> Actividad en periodo
              <TooltipBadge label="Flujo" tooltipText={METRIC_INFO_TEXTS.flujo} />
              <MetricInfo text={METRIC_INFO_TEXTS.company_flow} testId="info-company-flow" ariaLabel="Actividad en periodo" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={flow.series}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="new_opps" stroke="#3b82f6" name="Nuevas oportunidades" dot={NewOppsDot} />
                  <Line type="monotone" dataKey="activities" stroke="#10b981" name="Actividades registradas" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">Mes en curso marcado con punto vacío (parcial)</div>
          </CardContent>
        </Card>
      </div>

      {/* BLOCK 3: Requieren acción (5 tabs) */}
      <Card data-testid="block-action">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Requieren acción
            <MetricInfo text={METRIC_INFO_TEXTS.company_action_list} testId="info-action-list" ariaLabel="Requieren acción" />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={actionTab} onValueChange={setActionTab}>
            <TabsList className="mx-3 mt-3 flex-wrap h-auto" data-testid="action-tabs">
              <TabsTrigger value="prospects_no_activity" data-testid="tab-prospects_no_activity">Prospectos sin actividad ({requieren_accion.prospects_no_activity.rows.length})</TabsTrigger>
              <TabsTrigger value="cooling" data-testid="tab-cooling">Cuentas enfriándose ({requieren_accion.cooling.rows.length})</TabsTrigger>
              <TabsTrigger value="open_opps_stale" data-testid="tab-open_opps_stale">Opps abiertas sin actividad 30d ({requieren_accion.open_opps_stale.rows.length})</TabsTrigger>
              <TabsTrigger value="opps_missing_data" data-testid="tab-opps_missing_data">Opps sin importe/fecha ({requieren_accion.opps_missing_data.rows.length})</TabsTrigger>
              <TabsTrigger value="incomplete_records" data-testid="tab-incomplete_records">Fichas incompletas ({requieren_accion.incomplete_records.rows.length})</TabsTrigger>
            </TabsList>
            {['prospects_no_activity','cooling','open_opps_stale','opps_missing_data','incomplete_records'].map(k => (
              <TabsContent key={k} value={k} className="mt-0">
                <ActionTable rows={requieren_accion[k].rows} mode={k} onOpenCompany={handleOpenCompany} onExport={handleExportTab} />
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompaniesAnalytics;
